import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import * as XLSX from 'xlsx';
import { loadAlertDismissals, saveAlertDismissal } from '../supabase';

function fmt(v) {
  return (parseFloat(v) || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function downloadJobsExcel(jobs, filename) {
  const rows = jobs.map(j => ({
    'Office': j.office, 'Client': j.client, 'Job#': j.jobNo,
    'Job Type': j.jobType, 'Status': j.status, 'Days Open': j.daysOpen,
    'Est Revenue': j.estRev, 'Act Revenue': j.actRev, 'Act Cost': j.actCost, 'P/L': j.pl,
  }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Jobs');
  XLSX.writeFile(wb, filename);
}

// ─── MINI SPARKLINE ───────────────────────────────────────────────────────────
function MiniSparkline({ counts, isFallingOff }) {
  const max = Math.max(...counts, 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 24 }}>
      {counts.map((v, i) => (
        <div key={i} style={{
          width: 6, height: `${Math.max(2, (v / max) * 24)}px`,
          borderRadius: '2px 2px 0 0',
          background: i >= 6 ? (isFallingOff ? '#ef4444' : '#0e7c7b') : '#cbd5e1',
          opacity: i < 4 ? 0.5 : 1,
        }} />
      ))}
    </div>
  );
}

// ─── WEEKLY CHART ─────────────────────────────────────────────────────────────
function WeeklyChart({ weeklyStats, isSaudi }) {
  const data = isSaudi
    ? weeklyStats.map(w => ({ name: w.label, 'TLS Opened': w.tlsOpened, 'T-JED Opened': w.tjedOpened, 'TLS Closed': w.tlsClosed, 'T-JED Closed': w.tjedClosed }))
    : weeklyStats.map(w => ({ name: w.label, 'Opened': w.opened, 'Closed': w.closed }));
  return (
    <div className="chart-block">
      <h3 className="chart-title">{isSaudi ? 'Weekly Jobs — TLS vs T-JED' : 'Weekly Jobs — Dubai Operations'}</h3>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip /><Legend />
          {isSaudi ? (<>
            <Bar dataKey="TLS Opened" fill="#0e7c7b" />
            <Bar dataKey="T-JED Opened" fill="#7c3aed" />
            <Bar dataKey="TLS Closed" fill="#10b981" />
            <Bar dataKey="T-JED Closed" fill="#a78bfa" />
          </>) : (<>
            <Bar dataKey="Opened" fill="#0e7c7b" />
            <Bar dataKey="Closed" fill="#10b981" />
          </>)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── OFFICE KPI CARD ──────────────────────────────────────────────────────────
function OfficeCard({ office, jobs, label }) {
  const total = jobs.length;
  const closed = jobs.filter(j => j.status === 'Closed').length;
  const initiated = jobs.filter(j => j.status === 'Initiated').length;
  const stale30 = jobs.filter(j => j.isStaleOpen).length;
  const closureRate = total > 0 ? Math.round(closed / total * 100) : 0;
  const closureColor = closureRate >= 50 ? '#10b981' : closureRate >= 25 ? '#f59e0b' : '#ef4444';
  return (
    <div className="office-card">
      <div className="office-card-header">
        <span className="office-name">{label}</span>
        <span className="office-office-tag">{office}</span>
      </div>
      <div className="office-kpi-row">
        <div className="office-kpi"><span className="office-kpi-val">{total}</span><span className="office-kpi-label">Total Jobs</span></div>
        <div className="office-kpi"><span className="office-kpi-val" style={{ color: closureColor }}>{closureRate}%</span><span className="office-kpi-label">Closure Rate</span></div>
        <div className="office-kpi"><span className="office-kpi-val">{initiated}</span><span className="office-kpi-label">Open</span></div>
        <div className="office-kpi"><span className="office-kpi-val" style={{ color: stale30 > 0 ? '#ef4444' : '#1B2A3B' }}>{stale30}</span><span className="office-kpi-label">Stale 30d+</span></div>
      </div>
    </div>
  );
}

// ─── REVENUE GAP SUMMARY ──────────────────────────────────────────────────────
function RevenueGapSummary({ revenueGap, currency }) {
  const [showAll, setShowAll] = useState(false);
  if (!revenueGap || revenueGap.jobs.length === 0) return null;
  const clients = showAll ? revenueGap.clientSummary : revenueGap.clientSummary.slice(0, 4);
  const totalGap = revenueGap.clientSummary.reduce((s, c) => s + c.gapAmount, 0);

  return (
    <div className="chart-block" style={{ marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h3 className="chart-title" style={{ margin: 0 }}>📉 Estimate vs Actual Revenue Gap</h3>
        <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 700 }}>
          Total gap: {currency} {fmt(totalGap)} across {revenueGap.jobs.length} jobs
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {/* By client */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>By Client</div>
          {clients.map((c, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1B2A3B' }}>{c.client}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{c.jobCount} jobs · est {currency} {fmt(c.totalEst)} → act {currency} {fmt(c.totalAct)}</div>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>-{c.gapPct}%</span>
            </div>
          ))}
          {revenueGap.clientSummary.length > 4 && (
            <button onClick={() => setShowAll(!showAll)} style={{ fontSize: 11, color: '#0e7c7b', background: 'none', border: 'none', cursor: 'pointer', marginTop: 6 }}>
              {showAll ? 'Show less' : `+${revenueGap.clientSummary.length - 4} more clients`}
            </button>
          )}
        </div>
        {/* By job type */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>By Job Type</div>
          {revenueGap.jobTypeSummary.map((t, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f1f5f9' }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1B2A3B' }}>{t.jobType}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{t.jobCount} jobs · {currency} {fmt(t.gapAmount)} gap</div>
              </div>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#ef4444' }}>-{t.gapPct}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ALERT CARD WITH DISMISSAL ────────────────────────────────────────────────
function AlertCard({ alert, onDownload, entity, dismissals, onDismiss }) {
  const [expanded, setExpanded] = useState(false);
  const [showDismiss, setShowDismiss] = useState(false);
  const bgColor = alert.severity === 'red' ? '#fee2e2' : '#fef3c7';
  const borderColor = alert.severity === 'red' ? '#ef4444' : '#f59e0b';
  const icon = alert.severity === 'red' ? '🔴' : '🟡';

  // Get unique job types in this alert
  const jobTypes = alert.jobs
    ? [...new Set(alert.jobs.map(j => j.jobType).filter(Boolean))]
    : [];
  const currentExclusions = dismissals[alert.type] || [];

  return (
    <div className="history-alert" style={{ background: bgColor, borderLeft: `4px solid ${borderColor}` }}>
      <div className="history-alert-header" onClick={() => setExpanded(!expanded)}>
        <span>{icon} {alert.message}</span>
        <div style={{ display: 'flex', gap: 8 }}>
          {alert.jobs && alert.jobs.length > 0 && (
            <button className="btn-small" onClick={e => { e.stopPropagation(); onDownload(alert); }}>↓ Excel</button>
          )}
          {jobTypes.length > 0 && (
            <button className="btn-small" style={{ color: '#7c3aed' }} onClick={e => { e.stopPropagation(); setShowDismiss(!showDismiss); }}>
              Exclude types
            </button>
          )}
          {alert.jobs && alert.jobs.length > 0 && (
            <span className="expand-toggle">{expanded ? '▲' : '▼'}</span>
          )}
        </div>
      </div>
      <div className="history-alert-action">→ {alert.action}</div>

      {/* Dismiss by job type panel */}
      {showDismiss && jobTypes.length > 0 && (
        <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(255,255,255,0.6)', borderRadius: 6 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 6 }}>Exclude job types from this alert:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {jobTypes.map(jt => (
              <label key={jt} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={currentExclusions.includes(jt)}
                  onChange={e => {
                    const next = e.target.checked
                      ? [...currentExclusions, jt]
                      : currentExclusions.filter(x => x !== jt);
                    onDismiss(alert.type, next);
                  }}
                />
                {jt}
              </label>
            ))}
          </div>
          {currentExclusions.length > 0 && (
            <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 6 }}>
              ✓ {currentExclusions.join(', ')} excluded from this alert
            </div>
          )}
        </div>
      )}

      {expanded && alert.jobs && (
        <div className="history-alert-jobs">
          {alert.jobs.slice(0, 15).map((j, i) => (
            <div key={i} className="history-alert-job-row">
              <span className="job-office-tag">{j.office}</span>
              <span className="job-client">{j.client}</span>
              <span className="job-no">{j.jobNo}</span>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>{j.jobType}</span>
              {j.daysOpen !== null && <span className="job-days">{j.daysOpen}d</span>}
              {j.actRev > 0 && <span className="job-rev">{fmt(j.actRev)}</span>}
            </div>
          ))}
          {alert.jobs.length > 15 && (
            <div className="history-alert-more">+{alert.jobs.length - 15} more — download Excel</div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── CLIENT VOLUME TABLE ──────────────────────────────────────────────────────
function ClientVolumeTable({ historyJobs }) {
  const today = new Date();
  const weekStarts = Array.from({ length: 8 }, (_, w) =>
    new Date(today - (w + 1) * 7 * 24 * 60 * 60 * 1000).getTime()
  ).reverse();

  const byClient = {};
  historyJobs.forEach(j => {
    if (!byClient[j.client]) byClient[j.client] = [];
    byClient[j.client].push(j);
  });

  const clients = Object.keys(byClient).map(name => {
    const jobs = byClient[name];
    const weekCounts = weekStarts.map((start, i) => {
      const end = i < 7 ? weekStarts[i + 1] : today.getTime();
      return jobs.filter(j => j.createdDate && j.createdDate >= start && j.createdDate < end).length;
    });
    const totalJobs = jobs.length;
    const closedJobs = jobs.filter(j => j.status === 'Closed').length;
    const recent2 = weekCounts.slice(6).reduce((s, v) => s + v, 0);
    const prior2 = weekCounts.slice(4, 6).reduce((s, v) => s + v, 0);
    const isFallingOff = prior2 > 0 && recent2 === 0;
    const isDropping = prior2 > recent2 && recent2 > 0;
    const isGoneQuiet = weekCounts.slice(4).every(v => v === 0) && weekCounts.slice(0, 4).some(v => v > 0);
    const concern = isFallingOff ? 3 : isGoneQuiet ? 2 : isDropping ? 1 : 0;
    return { name, jobs, weekCounts, totalJobs, closedJobs, recent2, prior2, isFallingOff, isDropping, isGoneQuiet, concern };
  });

  const mostActive = [...clients].sort((a, b) => b.totalJobs - a.totalJobs);
  const mostConcerning = [...clients].filter(c => c.concern > 0).sort((a, b) => b.concern - a.concern || b.prior2 - a.prior2);

  const ClientRow = ({ client, showConcern }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#1B2A3B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{client.name}</div>
        <div style={{ fontSize: 11, color: '#6b7280' }}>{client.totalJobs} jobs · {client.closedJobs} closed</div>
      </div>
      <MiniSparkline counts={client.weekCounts} isFallingOff={client.isFallingOff} />
      {showConcern && (
        <div style={{ fontSize: 11, fontWeight: 600, color: client.isFallingOff ? '#ef4444' : client.isGoneQuiet ? '#ef4444' : '#f59e0b', minWidth: 80, textAlign: 'right' }}>
          {client.isFallingOff ? '⬇ dropped off' : client.isGoneQuiet ? '⚫ gone quiet' : client.isDropping ? '↘ slowing' : ''}
        </div>
      )}
    </div>
  );

  const [showMoreActive, setShowMoreActive] = useState(false);
  const [showMoreConcerning, setShowMoreConcerning] = useState(false);

  const activeList = showMoreActive ? mostActive : mostActive.slice(0, 4);
  const concernList = showMoreConcerning ? mostConcerning : mostConcerning.slice(0, 4);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 20 }}>
      <div className="chart-block" style={{ padding: 16 }}>
        <h3 className="chart-title" style={{ margin: 0, marginBottom: 10 }}>Most Active Clients</h3>
        {activeList.map((c, i) => <ClientRow key={i} client={c} showConcern={false} />)}
        {mostActive.length > 4 && (
          <button onClick={() => setShowMoreActive(!showMoreActive)} style={{ fontSize: 11, color: '#0e7c7b', background: 'none', border: 'none', cursor: 'pointer', marginTop: 8 }}>
            {showMoreActive ? 'Show less' : `+${mostActive.length - 4} more clients`}
          </button>
        )}
      </div>
      <div className="chart-block" style={{ padding: 16 }}>
        <h3 className="chart-title" style={{ margin: 0, marginBottom: 10 }}>⚠ Clients to Watch</h3>
        {mostConcerning.length === 0 ? (
          <div style={{ color: '#10b981', fontSize: 13 }}>✓ No clients showing drop-off signals</div>
        ) : (
          <>
            {concernList.map((c, i) => <ClientRow key={i} client={c} showConcern={true} />)}
            {mostConcerning.length > 4 && (
              <button onClick={() => setShowMoreConcerning(!showMoreConcerning)} style={{ fontSize: 11, color: '#0e7c7b', background: 'none', border: 'none', cursor: 'pointer', marginTop: 8 }}>
                {showMoreConcerning ? 'Show less' : `+${mostConcerning.length - 4} more`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── MAIN VIEW ────────────────────────────────────────────────────────────────
export default function SaudiOps({ historyJobs, weeklyStats, alerts, entity, ghostJobs, revenueGap }) {
  const currency = entity === 'uae' ? 'AED' : 'SAR';
  const isSaudi = entity === 'saudi';
  const [dismissals, setDismissals] = useState({});

  useEffect(() => {
    loadAlertDismissals(entity).then(setDismissals);
  }, [entity]);

  const handleDismiss = useCallback(async (alertType, excludedJobTypes) => {
    await saveAlertDismissal(entity, alertType, excludedJobTypes);
    setDismissals(prev => ({ ...prev, [alertType]: excludedJobTypes }));
  }, [entity]);

  if (!historyJobs || historyJobs.length === 0) {
    return (
      <div className="view saudi-ops empty-state">
        <div className="empty-icon">📋</div>
        <div className="empty-title">No Job History Data</div>
        <div className="empty-sub">Upload the 3-month job history file from SCM Profit to see trends, office comparison, and alerts.</div>
      </div>
    );
  }

  const mainJobs = entity === 'uae' ? historyJobs.filter(j => j.office !== 'DXB') : historyJobs;
  const tlsJobs = mainJobs.filter(j => j.office === 'TLS');
  const tjedJobs = mainJobs.filter(j => j.office === 'T-JED');
  const dubaiJobs = mainJobs.filter(j => j.office === 'DubaiTAX');
  const totalJobs = mainJobs.length;
  const totalClosed = mainJobs.filter(j => j.status === 'Closed').length;
  const totalStale = mainJobs.filter(j => j.isStaleOpen).length;
  const groupClosureRate = totalJobs > 0 ? Math.round(totalClosed / totalJobs * 100) : 0;

  // Filter alerts by dismissals
  const filteredAlerts = alerts ? alerts.map(a => {
    const excluded = dismissals[a.type] || [];
    if (excluded.length === 0 || !a.jobs) return a;
    const filteredJobs = a.jobs.filter(j => !excluded.includes(j.jobType));
    return { ...a, jobs: filteredJobs, message: a.message.replace(/^\d+/, filteredJobs.length) };
  }).filter(a => !a.jobs || a.jobs.length > 0) : [];

  // Ghost jobs alert — filter by dismissals
  const ghostExcluded = dismissals['ghost_jobs'] || [];
  const filteredGhostJobs = (ghostJobs || []).filter(j => !ghostExcluded.includes(j.jobType));
  const ghostAlert = filteredGhostJobs.length > 0 ? {
    type: 'ghost_jobs',
    severity: 'red',
    message: `${filteredGhostJobs.length} ghost jobs — opened but nothing has happened (no cost, no revenue, 30+ days)`,
    action: 'Review with ops immediately — these jobs have been forgotten',
    jobs: filteredGhostJobs,
  } : null;

  const allAlerts = [...(ghostAlert ? [ghostAlert] : []), ...filteredAlerts];

  const handleDownload = (alert) => {
    const today = new Date().toISOString().split('T')[0];
    downloadJobsExcel(alert.jobs, `Trejhara_${entity}_${alert.type}_${today}.xlsx`);
  };

  return (
    <div className="view saudi-ops">
      <div className="kpi-row">
        <div className="kpi">
          <span className="kpi-value">{totalJobs}</span>
          <span className="kpi-label">Total Jobs (3mo)</span>
          <span className="kpi-sub">{totalClosed} closed · {groupClosureRate}% rate</span>
        </div>
        <div className={`kpi ${totalStale > 10 ? 'danger' : totalStale > 0 ? 'warn' : ''}`}>
          <span className="kpi-value">{totalStale}</span>
          <span className="kpi-label">Open 30+ Days ⚠</span>
          <span className="kpi-sub">Action required</span>
        </div>
        <div className="kpi">
          <span className="kpi-value">{Object.keys(mainJobs.reduce((acc, j) => { acc[j.client] = true; return acc; }, {})).length}</span>
          <span className="kpi-label">Active Clients</span>
          <span className="kpi-sub">in last 3 months</span>
        </div>
      </div>

      {isSaudi ? (
        <div className="office-cards-row">
          <OfficeCard office="TLS" label="Haziq (TLS)" jobs={tlsJobs} />
          <OfficeCard office="T-JED" label="Shahalam (T-JED)" jobs={tjedJobs} />
        </div>
      ) : (
        <div className="office-cards-row" style={{ gridTemplateColumns: '1fr' }}>
          <OfficeCard office="DubaiTAX" label="Dubai Operations" jobs={dubaiJobs} />
        </div>
      )}

      {weeklyStats && weeklyStats.length > 0 && (
        <WeeklyChart weeklyStats={weeklyStats} isSaudi={isSaudi} />
      )}

      <ClientVolumeTable historyJobs={mainJobs} />

      {revenueGap && revenueGap.jobs.length > 0 && (
        <RevenueGapSummary revenueGap={revenueGap} currency={currency} />
      )}

      {allAlerts.length > 0 && (
        <div className="history-alerts-section" style={{ marginTop: 20 }}>
          <h3 className="section-title">⚠ Alerts</h3>
          {allAlerts.map((a, i) => (
            <AlertCard
              key={i}
              alert={a}
              onDownload={handleDownload}
              entity={entity}
              dismissals={dismissals}
              onDismiss={handleDismiss}
            />
          ))}
        </div>
      )}
    </div>
  );
}
